import React, { ReactNode } from 'react';
import styled from 'styled-components';
import { Tag } from '../Tags';
import { FlexRow } from '../FlexRow';
import { SpaceWrapper } from '../SpaceWrapper';
import { BoldSmallBodyText, TinyBodyText, Header1Text, SmallBodyText } from '../Typography';
import { Icon, IconSize, IconType } from '../Icon';
import { IHasTestData } from '../../test-utilities/IHasTestData';
import { Grid } from '../Grid';
import { HeaderSubItem } from './HeaderSubItem';
import { Tooltip } from '../Tooltip';
import { bullet } from '../../../utilities/order/magicStrings';
import { mergeTests } from '../../test-utilities/mergeTests';
import { ISelectOption } from '../Select';

interface IPageHeaderProps extends IHasTestData {
  /* The first data point will be larger and on top, the rest of the data points will be under it and smaller */
  topTag?: Tag;
  dataPoints: ReactNode[] | ISelectOption<ReactNode>[][];
  headerText?: string;
  headerTag?: Tag;
  bottomTags?: Tag[];
  orderLink?: { color: string; text?: string };
  isSprucePowered?: boolean;
  children?: ReactNode;
}

const PageHeaderWrapper = styled(SpaceWrapper)`
  display: grid;
  gap: ${(props) => props.theme.spacing.spacing5};
  padding-bottom: ${(props) => props.theme.spacing.roomyContentPaddingValue * 2}px;
`;

const StyledOrderLinkWrapper = styled.div`
  display: flex;
  align-items: center;
`;

export const PageHeader: React.FC<IPageHeaderProps> = ({
  topTag,
  bottomTags,
  dataPoints,
  headerTag,
  headerText,
  orderLink,
  'data-test-id': dataTestID,
  children,
  isSprucePowered,
}) => {
  let dataPointsArray: ISelectOption[][] = [];
  if (!Array.isArray(dataPoints[0])) {
    const row: ISelectOption[] = (dataPoints as string[]).map((point: string) => ({
      label: '',
      value: point,
    }));
    dataPointsArray.push(row);
  } else {
    dataPointsArray = dataPoints as ISelectOption[][];
  }

  return (
    <PageHeaderWrapper data-test-id={mergeTests(dataTestID, 'wrapper')}>
      {topTag && (
        <FlexRow
          verticalAlign={'center'}
          textAlign={'center'}
          justifyContent={'center'}
          data-test-id={mergeTests(dataTestID, 'top-tag')}
        >
          {topTag}
        </FlexRow>
      )}
      <FlexRow verticalAlign={'center'} textAlign={'center'} justifyContent={'center'}>
        {headerText ? <Header1Text data-test-id={mergeTests(dataTestID, 'header')}>{headerText}</Header1Text> : null}
        {headerTag}
        {orderLink && (
          <StyledOrderLinkWrapper>
            <Icon type={IconType.StackAlt} color={orderLink.color} />
            {orderLink.text && (
              <BoldSmallBodyText style={{ color: orderLink.color }} data-test-id={mergeTests(dataTestID, 'tag')}>
                {orderLink.text}
              </BoldSmallBodyText>
            )}
          </StyledOrderLinkWrapper>
        )}
      </FlexRow>
      <Grid>
        {bottomTags && (
          <FlexRow justifyContent={'center'}>
            {bottomTags.map((bottomTag) => (
              <>{bottomTag}</>
            ))}
          </FlexRow>
        )}
        <DataPointsDisplay dataPointsArray={dataPointsArray} isSprucePowered={isSprucePowered} />
        {children}
      </Grid>
    </PageHeaderWrapper>
  );
};

// Each sub-array is a "row" for the PageHeader to display.
const DataPointsDisplay: React.FC<{
  dataPointsArray: ISelectOption<ReactNode>[][];
  isSprucePowered?: boolean;
}> = ({ dataPointsArray, isSprucePowered }) => (
  <>
    {dataPointsArray.map((labelNodeArray: ISelectOption<ReactNode>[]) => (
      <FlexRow
        verticalAlign={'center'}
        textAlign={'center'}
        justifyContent={'center'}
        key={labelNodeArray[0]?.label || 'key'}
      >
        {labelNodeArray.map((dataPoint: ISelectOption<ReactNode>, index: number) => (
          <React.Fragment key={`${dataPoint.label}-${index}`}>
            <HeaderSubItem key={dataPoint.label} label={dataPoint.label} value={dataPoint.value} />
            {index < labelNodeArray.length - 1 ? (
              <SmallBodyText key={`dot-${index}`}>{bullet}</SmallBodyText>
            ) : (
              <SprucePoweredToolTip isSprucePowered={isSprucePowered} />
            )}
          </React.Fragment>
        ))}
      </FlexRow>
    ))}
  </>
);

const SprucePoweredToolTip: React.FC<{ isSprucePowered?: boolean }> = ({ isSprucePowered }) => (
  <>
    {isSprucePowered && (
      <SpaceWrapperAlignSelfStart topSpace={'none'} leftSpace={'none'} bottomSpace={'none'} rightSpace={'none'}>
        <Tooltip
          tooltipContent={<TinyBodyText>SprucePowered Order</TinyBodyText>}
          placement={'right'}
          trigger={'hover'}
          offset={'.3rem'}
        >
          <Icon type={IconType.SprucePowered} size={IconSize.Small} />
        </Tooltip>
      </SpaceWrapperAlignSelfStart>
    )}
  </>
);

const SpaceWrapperAlignSelfStart = styled(SpaceWrapper)`
  display: flex;
  align-self: start;
`;
